<template>
    <section id="contact" class="py-20 bg-muted/30">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center max-w-3xl mx-auto mb-16">
                <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full mb-4 uppercase text-sm tracking-wider font-medium">
                    {{ $t('contact.badge') }}
                </div>
                <h2 class="text-3xl sm:text-4xl mb-4">
                    {{ $t('contact.title') }}
                </h2>
                <p class="text-lg text-muted-foreground">
                    {{ $t('contact.subtitle') }}
                </p>
            </div>

            <form @submit.prevent="handleSubmit" class="space-y-6 bg-white border border-border rounded-2xl p-8" novalidate>
                <div class="grid md:grid-cols-2 gap-6">
                    <div class="order-1">
                        <label for="name" class="block text-sm font-medium mb-2">
                            {{ $t('contact.form.name') }} *
                        </label>
                        <div class="relative">
                            <UserIcon class="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                            <input
                                type="text"
                                id="name"
                                name="name"
                                v-model="form.name"
                                required
                                class="w-full pl-11 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                                :placeholder="$t('contact.form.namePlaceholder')"
                            />
                        </div>
                    </div>

                    <div class="order-3 md:order-2">
                        <label for="email" class="block text-sm font-medium mb-2">
                            {{ $t('contact.form.email') }} *
                        </label>
                        <div class="relative">
                            <EnvelopeIcon class="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                            <input
                                type="email"
                                id="email"
                                name="email"
                                v-model="form.email"
                                required
                                class="w-full pl-11 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                                :placeholder="$t('contact.form.emailPlaceholder')"
                            />
                        </div>
                    </div>

                    <div class="order-2 md:order-3">
                        <label for="company" class="block text-sm font-medium mb-2">
                            {{ $t('contact.form.company') }} *
                        </label>
                        <div class="relative">
                            <BuildingOfficeIcon class="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                            <input
                                type="text"
                                id="company"
                                name="company"
                                v-model="form.company"
                                required
                                class="w-full pl-11 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                                :placeholder="$t('contact.form.companyPlaceholder')"
                            />
                        </div>
                    </div>

                    <div class="order-4">
                        <label for="phone" class="block text-sm font-medium mb-2">
                            {{ $t('contact.form.phone') }}
                        </label>
                        <div class="relative">
                            <PhoneIcon class="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                v-model="form.phone"
                                class="w-full pl-11 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                                :placeholder="$t('contact.form.phonePlaceholder')"
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <label for="message" class="block text-sm font-medium mb-2">
                        {{ $t('contact.form.message') }}
                    </label>
                    <div class="relative">
                        <ChatBubbleLeftRightIcon class="absolute left-3 top-3 size-5 text-muted-foreground" />
                        <textarea
                            id="message"
                            name="message"
                            v-model="form.message"
                            rows="5"
                            class="w-full pl-11 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white resize-none"
                            :placeholder="$t('contact.form.message')"
                        />
                    </div>
                </div>

                <!-- Honeypot field (hidden from users, catches bots) -->
                <input
                    type="text"
                    name="website"
                    v-model="form.website"
                    autocomplete="off"
                    tabindex="-1"
                    class="absolute opacity-0 pointer-events-none"
                    aria-hidden="true"
                />

                <!-- Turnstile CAPTCHA -->
                <div class="flex justify-center">
                    <VueTurnstile
                        ref="turnstileRef"
                        v-model="turnstileToken"
                        :site-key="turnstilesiteKey"
                        @error="handleTurnstileError"
                    />
                </div>

                <button
                    type="submit"
                    :disabled="isSubmitting || !turnstileToken"
                    class="w-full bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-4 rounded-lg transition-all font-medium uppercase text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {{ isSubmitting ? $t('contact.form.sending') : $t('contact.form.submit') }}
                </button>

                <p class="text-sm text-muted-foreground text-center">
                    {{ $t('contact.form.privacyNotice.before') }}
                    <button
                        @click="openPrivacyModal"
                        class="text-sm text-accent hover:underline"
                        type="button"
                    >
                        {{ $t('contact.form.privacyNotice.privacy') }}
                    </button>
                    {{ $t('contact.form.privacyNotice.between') }}
                    <button
                        @click="openTermsModal"
                        class="text-sm text-accent hover:underline"
                        type="button"
                    >
                        {{ $t('contact.form.privacyNotice.terms') }}
                    </button>{{ $t('contact.form.privacyNotice.after') }}
                </p>
            </form>

            <!-- Success message -->
            <div v-if="submitSuccess" class="mt-6 max-w-3xl mx-auto flex items-center gap-4 p-4 bg-accent/10 border border-accent rounded-lg">
                <CheckCircleIcon class="size-6 text-accent shrink-0" />
                <p class="text-sm text-foreground">{{ $t('contact.success') }}</p>
            </div>

            <!-- Error message -->
            <div v-if="submitError" class="mt-6 max-w-3xl mx-auto flex items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <svg class="size-6 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p class="text-sm text-red-900">{{ submitError }}</p>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, inject } from 'vue';
import { UserIcon, EnvelopeIcon, BuildingOfficeIcon, ChatBubbleLeftRightIcon, CheckCircleIcon, PhoneIcon } from '@heroicons/vue/24/outline';
import VueTurnstile from 'vue-turnstile';
import { useI18n } from 'vue-i18n';

interface ContactForm {
    name: string;
    email: string;
    company: string;
    message: string;
    phone: string;
    website: string;
}

interface ApiErrorResponse {
    error: string;
    message: string;
}

interface LegalModals {
    openPrivacyModal: () => void;
    openTermsModal: () => void;
}

export default defineComponent({
    name: 'ContactSection',
    components: {
        UserIcon,
        EnvelopeIcon,
        BuildingOfficeIcon,
        ChatBubbleLeftRightIcon,
        CheckCircleIcon,
        PhoneIcon,
        VueTurnstile
    },
    setup() {
        const { t } = useI18n();
        const { openPrivacyModal, openTermsModal } = inject<LegalModals>('legalModals')!;

        const form = reactive<ContactForm>({
            name: '',
            email: '',
            company: '',
            message: '',
            phone: '',
            website: '' // Honeypot field
        });

        const isSubmitting = ref(false);
        const submitSuccess = ref(false);
        const submitError = ref<string | null>(null);
        const turnstileToken = ref<string>('');
        const turnstileRef = ref<{ reset: () => void } | null>(null);

        // Get configuration from environment variables
        const apiUrl = import.meta.env.VITE_CONTACT_API_URL || '';
        const turnstilesiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

        const handleTurnstileError = () => {
            console.error('Turnstile verification failed');
            submitError.value = t('contact.errors.captcha');
        };

        const handleSubmit = async () => {
            // Reset states
            submitSuccess.value = false;
            submitError.value = null;

            // Validate Turnstile token
            if (!turnstileToken.value) {
                submitError.value = t('contact.errors.captcha');
                return;
            }

            isSubmitting.value = true;

            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        name: form.name,
                        email: form.email,
                        company: form.company,
                        message: form.message,
                        phone: form.phone,
                        turnstileToken: turnstileToken.value,
                        website: form.website, // Honeypot
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json() as ApiErrorResponse;

                    // Map error codes to i18n messages
                    switch (errorData.error) {
                        case 'validation':
                            submitError.value = t('contact.errors.validation');
                            break;
                        case 'captcha':
                            submitError.value = t('contact.errors.captcha');
                            break;
                        case 'rate_limit':
                            submitError.value = t('contact.errors.rateLimit');
                            break;
                        default:
                            submitError.value = t('contact.errors.generic');
                    }

                    isSubmitting.value = false;
                    return;
                }

                // Success
                submitSuccess.value = true;

                // Reset form
                form.name = '';
                form.email = '';
                form.company = '';
                form.message = '';
                form.phone = '';
                form.website = '';
                turnstileRef.value?.reset();

                // Hide success message after 5 seconds
                setTimeout(() => {
                    submitSuccess.value = false;
                }, 5000);
            } catch (error) {
                console.error('Contact form submission error:', error);
                submitError.value = t('contact.errors.generic');
            } finally {
                isSubmitting.value = false;
            }
        };

        return {
            form,
            isSubmitting,
            submitSuccess,
            submitError,
            turnstileToken,
            turnstileRef,
            turnstilesiteKey,
            handleSubmit,
            handleTurnstileError,
            openPrivacyModal,
            openTermsModal
        };
    }
});
</script>
